import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhotoVideo, faSpinner } from '@fortawesome/pro-light-svg-icons';
import { FieldHookConfig, useField } from 'formik';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { faTrash, faX } from '@fortawesome/pro-regular-svg-icons';
import { useMutation } from '@apollo/client';
import UPLOAD_MEDIA from '../../graphql/UploadMedia.graphql';
import UPLOAD_PROFILE_MEDIA from '../../graphql/UploadProfileMedia.graphql';
import { MediaReference, ImageReference, MediaType, VideoReference } from '../../types/MediaReference';
import ErrorDisplay from '../ErrorDisplay';
import { faImage, faVideo } from '@fortawesome/pro-solid-svg-icons';
import { constants } from '../../constants';
import { GetMediaType } from '../utilities/GetMediaType';
import fileSize from 'filesize';


export const makeFileKey = (fileName: string) => `${Math.floor(Math.random() * 1000000)}-${fileName}`;

interface FileUploadProps {
    maxFiles?: number;
    maxSize?: number;
    label: string;
    initialFiles?: FileReference[];
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
}

export interface FileReference {
    // ID provided by the server for the media reference
    id?: string;
    // A random key for this file. Only used locally to individually track files
    // as uploading two with the same name would crash the page.
    key: string;
    // The local file reference
    localFile: File;
    // Preview image URL generated via createObjectURL
    previewUrl?: string;
    // Thumbnail blob to be uploaded (for videos only)
    thumbnailBlob?: Blob | null;
    // The upload status of the file
    uploadStatus: UploadStatus;
    // The filename returned by the API; this is also the relative path for the cloud storage service
    hostFilename?: string;
    // Some files are held for review (e.g. videos) which takes ages to check
    isHeldForReview?: boolean;
}

export interface RejectedFileReference extends FileReference {
    // Why this file was rejected. This could be set by us before uploading, or returned from the API
    rejectionReason: string;
}

export enum UploadStatus {
    // Used for video uploads, indicates we are still generating the thumbnail
    AwaitingThumbnail = 'AWAITING_THUMBNAIL',
    // The file has not started uploading
    NotStarted = 'NOT_STARTED',
    // The file is being uploaded
    InProgress = 'IN_PROGRESS',
    // The file has been uploaded
    Completed = 'COMPLETED',
    // There was an issue with the upload,
    Error = 'ERROR'
}

let currentVideo: File | null;

export default function FileUpload({ maxFiles, maxSize, label, initialFiles, setFieldValue, ...props }: FileUploadProps & FieldHookConfig<any>) {
    const [field, meta] = useField(props);
    const [files, setFiles] = useState<FileReference[]>(initialFiles || []);
    const [rejected, setRejected] = useState<RejectedFileReference[]>([]);
    const [videoSrc, setVideoSrc] = useState<string>();

    const [uploadFile, { error }] = useMutation<{ uploadPostMedia: MediaReference }>(UPLOAD_MEDIA);
    const [uploadProfileMedia, { error: uploadProfileMediaError }] = useMutation<{ uploadProfileMedia: ImageReference }, { file: File, mediaType: 'PROFILE_PICTURE' | 'BANNER' }>(UPLOAD_PROFILE_MEDIA);

    // set the maximum number of files to be uploaded
    const fileLimit = maxFiles ?? 6;
    // set the maximum total size (in bytes) of all files
    const sizeLimit = maxSize ?? 20000000; // 20 MB

    const onDropAcceptedHandler = (newFiles: File[]) => {
        const remainingAllocation = fileLimit - files.length;

        if (remainingAllocation > 0) {
            if (newFiles.length > remainingAllocation) {
                // the user has allocation remaining but is trying to upload too many files
                // trim the new files array to fit the allocation and reject the overflow
                newFiles.splice(remainingAllocation);

                // TODO: Show the user a warning
            }

            // Wrap the files with FileReference objects
            const fileReferences: FileReference[] = newFiles.map(file => ({
                // This is only used locally, so we don't care about anything crypto-secure
                key: makeFileKey(file.name),
                localFile: file,
                previewUrl: file.type.startsWith('video/') ? generateVideoThumbnail(file) : URL.createObjectURL(file),
                uploadStatus: file.type.startsWith('video/') ? UploadStatus.AwaitingThumbnail : UploadStatus.NotStarted
            }));

            // Merge the references into the files state
            setFiles(existingFiles => [...existingFiles, ...fileReferences]);
        } else {
            // TODO: Show the user a warning
        }
    }

    const onDropRejectedHandler = (newRejectedFiles: FileRejection[]) => {
        const newRejectedFileReferences: RejectedFileReference[] = newRejectedFiles.map(rejectedFile => ({
            key: makeFileKey(rejectedFile.file.name),
            localFile: rejectedFile.file,
            uploadStatus: UploadStatus.Error,
            rejectionReason: rejectedFile.errors.find(o => o)?.message ?? ''
        }));
        setRejected(rejectedFiles => [...rejectedFiles, ...newRejectedFileReferences]);
    }

    // Video thumbnail generation

    const videoContainer = useRef<HTMLVideoElement>(null);
    const canvasContainer = useRef<HTMLCanvasElement>(null);

    const generateVideoThumbnail = (file: File): string | undefined => {
        // Start the generation task
        buildVideoThumbnail(file);

        // Thumbnail generation is async so set the preview to an empty string for now
        // The above task will then update the state once the thumbnail is generated
        return undefined;
    }

    const buildVideoThumbnail = async (file: File) => {
        currentVideo = file;
        setVideoSrc(URL.createObjectURL(file));
    }

    const canPlayHandler = () => {
        if (canvasContainer.current && videoContainer.current) {
            canvasContainer.current.width = videoContainer.current?.videoWidth || 0;
            canvasContainer.current.height = videoContainer.current?.videoHeight || 0;

            // Set the video timestamp to 00:00:01 (1s)
            // This will trigger the timeUpdateHandler
            videoContainer.current.currentTime = 1;
        }
    }

    const timeUpdateHandler = () => {
        // Verify the canvas and video refs are loaded and that the current time is greater than 0s
        // The time update handler will fire initially at 0s before the loadedmetadata event has a chance to complete
        // The time will only progress to 1s AFTER the loadedmetadata event
        if (canvasContainer.current && videoContainer.current && videoContainer.current.currentTime > 0) {
            canvasContainer.current.getContext('2d')?.drawImage(videoContainer.current, 0, 0, videoContainer.current.videoWidth, videoContainer.current.videoHeight);
            videoContainer.current.pause();

            if (currentVideo) {
                // Build the thumbnail blob from the canvas
                canvasContainer.current?.toBlob(blob => {
                    if (blob) {
                        // The blob has been generated
                        // Build a local ObjectURL
                        const objectUrl = URL.createObjectURL(blob);

                        // Update the files state
                        setFiles(existingFiles => [...existingFiles].reduce((prev, current) => {
                            if (current.localFile.name === currentVideo?.name) {
                                current.previewUrl = objectUrl;
                                current.thumbnailBlob = blob;
                                current.uploadStatus = UploadStatus.NotStarted;
                            }
                            // push the current record to the previous array and return
                            prev.push(current);
                            return prev;
                        }, [] as FileReference[]));

                        currentVideo = null;
                        setVideoSrc(undefined);
                    }
                });
            }
        }
    }

    const progressHandler = () => {
        if (videoContainer.current) {
            videoContainer.current.play();
            videoContainer.current.removeEventListener('progress', progressHandler);
        }
    }

    // End of thumbnail generation components

    const removeFile = (file: any) => {
        setFiles([...files].filter(f => f !== file));
    }

    const uploadFiles = useCallback(async (filesToUpload: FileReference[]) => {
        //const newFiles = [...files];

        // Update the files state with the new status
        // Since the status will now be InProgress, the uploadFiles function should not be called
        // This could result in an infinite loop if the status is not able to update for some reason
        setFiles(existingFiles => {
            const newFiles = [...existingFiles];

            // Set the upload status for each file to InProgress
            for (let file of filesToUpload) {
                const target = newFiles.find(f => f.localFile.name === file.localFile.name);

                if (target)
                    target.uploadStatus = UploadStatus.InProgress;
            }

            return newFiles;
        });

        // Iterate through each file and call the uploadFile mutation to upload the file via the GraphQL API
        // The API will upload the file to the cloud storage and return a new filename
        filesToUpload.forEach(fileToUpload => {
            const mediaType = fileToUpload.localFile.type.startsWith('video/') ? MediaType.Video : MediaType.Image;

            if (field.name === 'background') {
                // Custom upload handler for background/banner images
                uploadProfileMedia({
                    variables: {
                        file: fileToUpload.localFile,
                        mediaType: 'BANNER'
                    }
                })
                    .then(result => {
                        setFiles(existingFiles => existingFiles.map(existingFileReference =>
                            existingFileReference.localFile === fileToUpload.localFile ? {
                                ...existingFileReference,
                                uploadStatus: UploadStatus.Completed,
                                hostFilename: result.data?.uploadProfileMedia.url,
                                previewUrl: existingFileReference.previewUrl,
                            } : existingFileReference))
                    });
            } else {
                uploadFile({
                    variables: {
                        file: fileToUpload.localFile,
                        videoThumbnail: fileToUpload.thumbnailBlob
                    }
                })
                    .catch((error) => {
                        setFiles(existingFiles => existingFiles.filter(file => file !== fileToUpload));
                        var rejectedFile: RejectedFileReference;
                        // @ts-ignore
                        const graphQlError = error.networkError.result.errors.find(e => e.extensions.BadFile);
                        if (graphQlError) {
                            rejectedFile = { ...fileToUpload, rejectionReason: graphQlError.message };
                        }
                        else {
                            rejectedFile = { ...fileToUpload, rejectionReason: 'An unknown error occurred.' };
                        }
                        setRejected(rejectedFiles => [...rejectedFiles, rejectedFile]);
                    })
                    .then(result => {
                        if (!result) return;
                        // update the state
                        setFiles(existingFiles => existingFiles.map(existingFileReference =>
                            existingFileReference.localFile === fileToUpload.localFile ? {
                                ...existingFileReference,
                                id: result.data?.uploadPostMedia.id,
                                uploadStatus: UploadStatus.Completed,
                                hostFilename: result.data?.uploadPostMedia.url,
                                previewUrl: mediaType === MediaType.Video ? (result.data?.uploadPostMedia as VideoReference)?.thumbnailUrl : existingFileReference.previewUrl,
                                isHeldForReview: result.data?.uploadPostMedia.isHeldForReview,
                            } : existingFileReference));
                    });
            }
        });
    }, [field.name, uploadFile, uploadProfileMedia]);

    useEffect(() => {
        // Identify files awaiting upload
        const awaitingUpload = files.filter(f => f.uploadStatus === UploadStatus.NotStarted);

        if (awaitingUpload.length) {
            // Initiate the upload for these files (async)
            uploadFiles(awaitingUpload);
        }

        // Update the formik field value whenever the local files state changes
        setFieldValue(props.name, files);
    }, [files, props.name, uploadFiles, setFieldValue]);

    const { isDragActive, getRootProps, getInputProps } = useDropzone({
        noClick: true,
        maxFiles: fileLimit,
        maxSize: sizeLimit,
        accept: 'image/*, video/mp4, video/quicktime',
        onDropAccepted: onDropAcceptedHandler,
        onDropRejected: onDropRejectedHandler
    });

    const getImagePreviewUrl = (file: FileReference) => {
        if (file.uploadStatus === UploadStatus.Completed && GetMediaType(file) === MediaType.Video) {
            return constants.cdnBaseUrl + '/' + file.previewUrl;
        }
        return file.previewUrl;
    }

    // @ts-ignore
    const gqlErrors = error?.networkError?.result.errors.filter(error => !error.extensions.BadFile);
    return (
        <div>
            <label className='block text-sm font-medium text-gray-700'>{label}</label>
            <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 ${isDragActive ? 'border-loving-500' : 'border-gray-300'} border-dashed rounded-md`} {...getRootProps()}>
                <div className={`space-y-1 text-center ${(isDragActive || files.length == fileLimit) && 'opacity-50'}`}>
                    <FontAwesomeIcon icon={faPhotoVideo} size='2x' className='text-gray-400 mx-auto mb-2' />
                    <div className='flex text-sm justify-center text-gray-600'>
                        <label htmlFor={`media_upload_${field.name}`} className={`relative ${files.length < fileLimit && 'cursor-pointer text-loving-600 hover:text-loving-500'} bg-white rounded-md font-medium focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-loving-500`}>
                            <span>{fileLimit > 1 ? 'Upload files' : 'Upload file'}</span>
                            <input type='file' id={`media_upload_${field.name}`} name={`media_upload_${field.name}`} className='sr-only' disabled={files.length >= fileLimit} {...getInputProps()} />
                        </label>
                        <p className='pl-1'>or drag and drop</p>
                    </div>
                    <p className='text-xs text-gray-500'>Maximum of {fileLimit} file{fileLimit == 1 ? '' : 's'} and {sizeLimit / (1000 * 1000)}MB total</p>
                </div>
            </div>

            {rejected.length > 0 && (
                <div className='text-sm mt-4 bg-red-500 rounded-md p-4'>
                    <div className='flex flex-row text-red-50'>
                        <span className='font-semibold flex-1'>{rejected.length} file{rejected.length > 1 && "s"} could not be uploaded:</span>
                        <button type='button' onClick={() => setRejected([])}>
                            <FontAwesomeIcon icon={faX} className='mx-1 my-1' />
                        </button>
                    </div>
                    <ul className='mt-2 text-red-50 text-xs leading-normal'>
                        {rejected.map(rejectedFile => (
                            <li key={rejectedFile.key}>
                                <span className='font-medium'>{rejectedFile.localFile.name}</span>
                                <span className='mr-1'>:</span>
                                <span>{rejectedFile.rejectionReason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <ul className='mt-2 space-y-1'>
                {files.map(f => (
                    <li className='flex w-full h-16 rounded-md shadow-md overflow-hidden' key={f.key}>
                        <div className='w-24 h-16 bg-gray-200 relative'>
                            <img className='object-cover w-full h-full' src={getImagePreviewUrl(f)} alt='' />
                            {f.uploadStatus === UploadStatus.InProgress && (
                                <div className='absolute inset-0 flex items-center justify-center bg-gray-600 bg-opacity-80'>
                                    <FontAwesomeIcon icon={faSpinner} size='lg' className='animate-spin text-white' />
                                </div>
                            )}
                        </div>
                        <div className='flex w-full min-w-0 items-center'>
                            <FontAwesomeIcon icon={f.localFile.type.startsWith('video/') ? faVideo : faImage} className='text-loving-500 m-4' />
                            <div className='flex-grow min-w-0'>
                                <div className='text-xs text-gray-800 font-medium whitespace-nowrap overflow-ellipsis overflow-hidden'>{f.localFile.name}</div>
                                <div className='text-xs text-gray-500'>
                                    {fileSize(f.localFile.size)}
                                    {f.uploadStatus === UploadStatus.InProgress && <span>&nbsp;&bull; Uploading...</span>}
                                    {f.uploadStatus === UploadStatus.NotStarted && <span>&nbsp;&bull; Pending upload...</span>}
                                    {f.uploadStatus === UploadStatus.AwaitingThumbnail && <span>&nbsp;&bull; Generating thumbnail...</span>}
                                    {f.uploadStatus === UploadStatus.Error && <span className='text-red-500'>&nbsp;&bull; Error</span>}
                                </div>
                                {f.isHeldForReview &&
                                    <div className='text-xs text-red-500'>
                                        This file may not appear for up to 60 minutes while it is checked.
                                    </div>
                                }
                            </div>
                            <div className='flex-none'>
                                <button
                                    type='button'
                                    disabled={f.uploadStatus !== UploadStatus.Completed}
                                    className='mx-5 my-3 leading-none text-gray-700 hover:text-red-500 disabled:text-gray-300 disabled:cursor-auto'
                                    onClick={() => { if (f.uploadStatus === UploadStatus.Completed) { removeFile(f); } }}
                                >
                                    <FontAwesomeIcon icon={faTrash} className='mx-1 my-1' />
                                </button>
                            </div>
                        </div>
                    </li>
                ))}
            </ul>

            {gqlErrors && gqlErrors.length > 0 && (
                <div className='max-w-lg mt-2'>
                    {/* @ts-ignore */}
                    <ErrorDisplay errors={gqlErrors.map(error => error.message)} />
                </div>
            )}

            <video ref={videoContainer} src={videoSrc} className='w-px h-px fixed bottom-0 right-0' onCanPlayThrough={canPlayHandler} onTimeUpdate={timeUpdateHandler} onProgress={progressHandler} />
            <canvas ref={canvasContainer} className='w-px h-px fixed bottom-0 right-0'>
            </canvas>

        </div>
    )
}
